import { Observable } from 'rxjs';
import { Permission } from '../data/models/permission.model';
import { UserGroup } from '../data/models/user-group.model';
import { UserRole } from '../data/models/user-role.model';
import { SortModel } from '../data/models/sort-model.model';
import httpService from './httpService';

class SecurityService {
  private readonly apiBase;

  constructor() {
    const { __environment__ } = window as any;
    this.apiBase = __environment__.REACT_APP_USER_API_URL.trim();
  }

  public getUserPermissions(
    search: string,
    sortModel: SortModel | null,
  ): Observable<Permission[]> {
    let url = `${this.apiBase}/permissions?userPermissionState=active,inactive&search=${encodeURIComponent(search)}`;
    if (sortModel) {
      url += `&sort=${sortModel.column}&sortOrder=${sortModel.direction}`;
    }
    return httpService.getJSON(url);
  }

  public getUserGroups(
    search: string,
    sortModel?: SortModel | null,
  ): Observable<UserGroup[]> {
    let url = `${this.apiBase}/groups?userGroupState=active,inactive&search=${encodeURIComponent(search)}`;
    if (sortModel) {
      url += `&sort=${sortModel.column}&sortOrder=${sortModel.direction}`;
    }
    return httpService.getJSON(url);
  }

  public createUserPermission(permission: Permission): Observable<Permission> {
    return httpService.postJSON(`${this.apiBase}/permissions`, permission);
  }

  public updateUserPermission(permission: Permission): Observable<Permission> {
    return httpService.putJSON(
      `${this.apiBase}/permissions/${permission.userPermissionID}`,
      permission,
    );
  }

  public deleteUserPermission(permissionID: number): Observable<Permission> {
    return httpService.deleteJSON(
      `${this.apiBase}/permissions/${permissionID}`,
    );
  }

  public createUserGroup(group: UserGroup): Observable<UserGroup> {
    return httpService.postJSON(`${this.apiBase}/groups`, group);
  }

  public updateUserGroup(group: UserGroup): Observable<UserGroup> {
    return httpService.putJSON(
      `${this.apiBase}/groups/${group.userGroupID}`,
      group,
    );
  }

  public deleteUserGroup(groupID: number): Observable<void> {
    return httpService.deleteJSON(`${this.apiBase}/groups/${groupID}`);
  }

  public getUserRoles(
    categoryType?: number,
    appTitle?: number,
    search?: string,
    sortModel?: SortModel | null,
  ): Observable<UserRole[]> {
    const catType = categoryType ? `?userCategoryTypeID=${categoryType}` : '';
    const app = appTitle
      ? categoryType
        ? `&applicationTitleID=${appTitle}`
        : `?applicationTitleID=${appTitle}`
      : '';
    const state =
      catType || app
        ? '&userRoleState=active,inactive'
        : '?userRoleState=active,inactive';
    let url = `${this.apiBase}/roles${catType}${app}${state}`;
    if (search && search.trim() !== '') {
      url += `&search=${encodeURIComponent(search)}`;
    }
    if (sortModel) {
      url += `&sort=${sortModel.column}&sortOrder=${sortModel.direction}`;
    }
    return httpService.getJSON(url);
  }

  public createUserRole(role: UserRole): Observable<UserRole> {
    return httpService.postJSON(`${this.apiBase}/roles`, role);
  }

  public updateUserRole(role: UserRole): Observable<UserRole> {
    return httpService.putJSON(
      `${this.apiBase}/roles/${role.userRoleID}`,
      role,
    );
  }

  public deleteUserRole(roleID: number): Observable<UserRole> {
    return httpService.deleteJSON(`${this.apiBase}/roles/${roleID}`);
  }
}

const securityService = new SecurityService();

export default securityService;
